import { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Button,
  Typography,
  TextField,
  Grid,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchStatesAsync,
  selectAllStates,
  selectStatesLoading,
  selectStatesError,
} from "../../../../Slices/statesSlice";
import {
  isAuthenticated,
  getStudentInfo,
  updateStudentInfo,
  getDocumentsByStudentId,
} from "../../../../ApiService/ApiService";
import {
  isValidPin,
  isValidPhoneNumber,
  isValidAadhar,
} from "../../../../Utils/validFormDetails";
import {
  ArrowBack,
  ArrowForward,
  Edit,
  Update,
  Upload,
  Person,
} from "@mui/icons-material";
import UploadDocuments from "./UploadDocuments";
import UpdateDocuments from "./UpdateDocuments";
import CustomDialog from "../../../../Components/CustomDialog";
import StudentInfo from "./StudentInfo";

function UpdateInfo({ setActionComponent, selectedStudentId, students, fetchStudentsByClassId }) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchStatesAsync());
  }, [dispatch]);

  const states = useSelector(selectAllStates);
  const statesLoading = useSelector(selectStatesLoading);
  const statesError = useSelector(selectStatesError);
  const [tabValue, setTabValue] = useState(0);
  const [studentDocuments, setStudentsDocuments] = useState([]);
  const [studentInfo, setStudentInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const fetchStudentsDocumentsData = async (selectedStudentId) => {
    try {
      const response = await getDocumentsByStudentId(selectedStudentId);
      setStudentsDocuments(response?.data || []);
    } catch (error) {
      console.log("Error fetching data:", error);
    }
  };

  const fetchStudentInfo = async () => {
    try {
      const response = await getStudentInfo(selectedStudentId);
      // console.log("STUDENT INFO", response?.data);
      setStudentInfo(response?.data?.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchStudentsDocumentsData(selectedStudentId);
    fetchStudentInfo(selectedStudentId);
  }, [selectedStudentId]);

  const [formData, setFormData] = useState({
    fatherEmail: "",
    fatherName: "",
    fatherPhone: "",
    fatherAadhar: "",
    fatherOccupation: "",
    motherEmail: "",
    motherName: "",
    motherPhone: "",
    motherAadhar: "",
    motherOccupation: "",
    street1: "",
    street2: "",
    city: "",
    stateId: "",
    pin: "",
    studentId: selectedStudentId,
  });

  useEffect(() => {
    setFormData({
      fatherEmail: studentInfo?.fatherEmail || "",
      fatherName: studentInfo?.fatherName || "",
      fatherPhone: studentInfo?.fatherPhone || "",
      fatherAadhar: studentInfo?.fatherAadhar || "",
      fatherOccupation: studentInfo?.fatherOccupation || "",
      motherEmail: studentInfo?.motherEmail || "",
      motherName: studentInfo?.motherName || "",
      motherPhone: studentInfo?.motherPhone || "",
      motherAadhar: studentInfo?.motherAadhar || "",
      motherOccupation: studentInfo?.motherOccupation || "",
      street1: studentInfo?.address?.street1 || "",
      street2: studentInfo?.address?.street1 || "",
      city: studentInfo?.address?.city || "",
      stateId: selectedState?.id || "",
      pin: studentInfo?.address?.pin || "",
      preferredCommunication: studentInfo?.preferredCommunication || "",
      studentId: selectedStudentId,
    });
  }, [studentInfo]);

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (
      !(
        formData.fatherName ||
        formData.fatherEmail ||
        formData.fatherPhone ||
        formData.fatherAadhar ||
        formData.fatherOccupation ||
        formData.motherName ||
        formData.motherEmail ||
        formData.motherPhone ||
        formData.motherAadhar ||
        formData.motherOccupation
      )
    ) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please provide either father details or mother details",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });

      return;
    }

    if (
      (formData.fatherAadhar && !isValidAadhar(formData.fatherAadhar)) ||
      (formData.motherAadhar && !isValidAadhar(formData.motherAadhar))
    ) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid aadhar number",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });

      return;
    }
    if (
      (formData.fatherPhone && !isValidPhoneNumber(formData.fatherPhone)) ||
      (formData.motherPhone && !isValidPhoneNumber(formData.motherPhone))
    ) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid 10-digit phone number",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });

      return;
    }
    if (!isValidPin(formData.pin)) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid 6-digit pincode",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }

    try {
      if (isAuthenticated()) {
        const response = await updateStudentInfo(formData);
        // console.log("Res", response?.data);
        if (response) {
          if (response?.data?.status === 200) {
            setDialog({
              visible: true,
              title: "Success",
              message: "Student Information updated",
              buttons: [{ name: "OK", onPress: handleDialogClose }],
            });
            setFormData({
              fatherEmail: "",
              fatherName: "",
              fatherPhone: "",
              fatherAadhar: "",
              fatherOccupation: "",
              motherEmail: "",
              motherName: "",
              motherPhone: "",
              motherAadhar: "",
              motherOccupation: "",
              street1: "",
              street2: "",
              city: "",
              stateId: "",
              pin: "",
              studentId: selectedStudentId,
            });
            setActionComponent(null);
          } else {
            const errorMessage =
              response?.data?.errorMessage || "Something went wrong";
            setDialog({
              visible: true,
              title: "Error",
              message: errorMessage,
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          setDialog({
            visible: true,
            title: "Error",
            message: "Invalid response from the server",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Unauthorized action",
          message: "Please login and try again",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "preferredCommunication") {
      setFormData({ ...formData, preferredCommunication: parseInt(value) });
    } else {
      if (
        name === "fatherPhone" ||
        name === "fatherAadhar" ||
        name === "motherPhone" ||
        name === "motherAadhar"
      ) {
        if (!/^\d*$/.test(value)) {
          return;
        }
      }
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const selectedState = states.find(
    (state) => state.name === studentInfo?.address?.state
  );

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box
        sx={{
          width: "75%",
          marginBottom: 3,
          marginLeft: "auto",
          marginRight: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="updateInfoTabs"
        >
          <Tab
            label="Update Student Information"
            icon={<Person fontSize="small" />}
            iconPosition="start"
            sx={{ textTransform: "unset" }}
          />
          <Tab
            label="Update Information"
            icon={<Edit fontSize="small" />}
            iconPosition="start"
            sx={{ textTransform: "unset" }}
          />
          <Tab
            label="Upload Documents"
            icon={<Upload fontSize="small" />}
            iconPosition="start"
            sx={{ textTransform: "unset" }}
          />
          <Tab
            label="Update Documents"
            icon={<Update fontSize="small" />}
            iconPosition="start"
            sx={{ textTransform: "unset" }}
          />
        </Tabs>

        {tabValue === 0 && (
          <StudentInfo
            setActionComponent={setActionComponent}
            students={students}
            selectedStudentId={selectedStudentId}
            fetchStudentsByClassId={fetchStudentsByClassId}
          />
        )}
        {tabValue === 1 && (
          <Paper
            elevation={3}
            sx={{
              padding: 3,
              marginTop: 2,
              marginBottom: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Father's Email"
                    name="fatherEmail"
                    value={formData.fatherEmail}
                    onChange={handleChange}
                    size="small"
                    disabled={studentInfo?.fatherEmail}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Father's Name"
                    name="fatherName"
                    value={formData.fatherName}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Father's Phone"
                    name="fatherPhone"
                    value={formData.fatherPhone}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <TextField
                    fullWidth
                    label="Father's Aadhar"
                    name="fatherAadhar"
                    value={formData.fatherAadhar}
                    onChange={handleChange}
                    size="small"
                    disabled={studentInfo?.fatherAadhar}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <TextField
                    fullWidth
                    label="Father's Occupation"
                    name="fatherOccupation"
                    value={formData.fatherOccupation}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Mother's Email"
                    name="motherEmail"
                    value={formData.motherEmail}
                    onChange={handleChange}
                    size="small"
                    disabled={studentInfo?.motherEmail}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Mother's Name"
                    name="motherName"
                    value={formData.motherName}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Mother's Phone"
                    name="motherPhone"
                    value={formData.motherPhone}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <TextField
                    fullWidth
                    label="Mother's Aadhar"
                    name="motherAadhar"
                    value={formData.motherAadhar}
                    onChange={handleChange}
                    size="small"
                    disabled={studentInfo?.motherAadhar}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <TextField
                    fullWidth
                    label="Mother's Occupation"
                    name="motherOccupation"
                    value={formData.motherOccupation}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <TextField
                    fullWidth
                    label="Street 1"
                    name="street1"
                    value={formData.street1}
                    onChange={handleChange}
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <TextField
                    fullWidth
                    label="Street 2"
                    name="street2"
                    value={formData.street2}
                    onChange={handleChange}
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <TextField
                    fullWidth
                    label="PIN"
                    name="pin"
                    value={formData.pin}
                    onChange={handleChange}
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <FormControl fullWidth required size="small">
                    <InputLabel id="state">State</InputLabel>
                    <Select
                      name="stateId"
                      label="State"
                      labelId="state"
                      value={formData.stateId}
                      onChange={handleChange}
                      size="small"
                      disabled={statesLoading || statesError}
                    >
                      {statesLoading ? (
                        <MenuItem value="">Loading...</MenuItem>
                      ) : statesError ? (
                        <MenuItem value="">Error fetching states</MenuItem>
                      ) : (
                        states.map((state) => (
                          <MenuItem key={state?.id} value={state?.id}>
                            {state?.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      Preferred Communication
                    </FormLabel>
                    <RadioGroup
                      aria-label="preferredCommunication"
                      name="preferredCommunication"
                      value={formData.preferredCommunication || ""}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="1"
                        control={<Radio disabled={!formData.fatherPhone} />}
                        label="Father"
                      />
                      <FormControlLabel
                        value="2"
                        control={<Radio disabled={!formData.motherPhone} />}
                        label="Mother"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="inherit"
                    onClick={() => setActionComponent(null)}
                    startIcon={<ArrowBack />}
                    sx={{ textTransform: "unset" }}
                  >
                    Back
                  </Button>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForward />}
                    disabled={loading}
                    sx={{ textTransform: "unset" }}
                  >
                    {loading ? "Loading.." : "Submit"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}
        {tabValue === 2 && (
          <UploadDocuments
            setActionComponent={setActionComponent}
            selectedStudentId={selectedStudentId}
            studentDocuments={studentDocuments}
          />
        )}
        {tabValue === 3 && (
          <UpdateDocuments
            setActionComponent={setActionComponent}
            selectedStudentId={selectedStudentId}
            studentDocuments={studentDocuments}
            setStudentDocuments={setStudentsDocuments}
          />
        )}
      </Box>
    </>
  );
}

export default UpdateInfo;
